import {
  type AreaWeatherForecast,
  type DailyForecastSelection,
  WeatherSource,
} from '@ebalance/proto/bffuser/v1/weather_forecast_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  buildKomaTemperatureForecastValues,
  buildKomaTemperatureLabels,
  buildKomaWeatherIcons,
} from '~/models/weather/koma'

export type AreaWeatherIcons = {
  weatherIcons: string[]
  weatherTemps: string[]
  weatherTempValues: Array<number | null>
}

const EMPTY_WEATHER_ICONS: AreaWeatherIcons = {
  weatherIcons: [],
  weatherTemps: [],
  weatherTempValues: [],
}

/** targetDate と同じ年月日 (google.type.Date) を指す DailyForecastSelection を探す。 */
function findSelection(
  selections: DailyForecastSelection[],
  targetDate: ProtoDate,
): DailyForecastSelection | undefined {
  return selections.find(
    (s) =>
      s.targetDate !== undefined &&
      s.targetDate.year === targetDate.year &&
      s.targetDate.month === targetDate.month &&
      s.targetDate.day === targetDate.day,
  )
}

/**
 * 1 エリアの予報応答 + 対象日から天気アイコン帯 (30 分刻み 48 個の絵文字列、
 * doji-bg §48 コマ準拠) と予想気温ラベル (同 48 スロット、正時のみ整数 ℃・他
 * 30 分位置は空)、および気象チャートの予想気温折れ線用の数値系列 (同 48 スロット、
 * 発表値のまま) を組み立てる。時系列予報 (VPFD) の天気・気温は正時の点のコマに
 * だけ値を持ち、点以外のコマは空文字 / null (AC-WD-003a)。有効な気温点が 1 つも
 * 無い場合は全 null 配列。
 *
 * 対象日の `daily_selection` (spec AC-WD-009/010 優先順位で backend が解決済み)
 * を lookup し、`weather_source` で表示元 series を切り替える:
 * VPFD → `weather3hour` の正時の点のコマにだけ置く / PREFECTURAL・WEEKLY →
 * 日別 1 天気には正時の点が無いので予報アイコンを置かない (doji-bg AC-BDB-002 /
 * AC-BDB-010b。日別 1 値を時間帯別のコマへ複製しない) /
 * UNAVAILABLE・対象日の selection が無い → 空 (気象帯 hidden 相当)。
 * `weatherTemps` / `weatherTempValues` は selection と無関係に常に
 * `forecast.temperature` (VPFD 気温シリーズ) から生成する — 府県 / 週間選択日は
 * 該当日に VPFD 気温行が無いため自然に空になる。
 *
 * 応答なし・地点未対応または取得失敗 (is_available=false) は空配列に降格し、
 * チャート本体の表示は妨げない (doji-bg AC-372 相当)。
 */
export function buildAreaWeatherIcons(
  forecast: AreaWeatherForecast | undefined,
  targetDate: ProtoDate,
): AreaWeatherIcons {
  if (!forecast?.isAvailable) {
    return EMPTY_WEATHER_ICONS
  }
  const weatherTemps = buildKomaTemperatureLabels(forecast.temperature)
  const weatherTempValues = buildKomaTemperatureForecastValues(
    forecast.temperature,
  )
  const selection = findSelection(forecast.dailySelection, targetDate)
  switch (selection?.weatherSource) {
    case WeatherSource.VPFD:
      return {
        weatherIcons: buildKomaWeatherIcons(forecast.weather3hour),
        weatherTemps,
        weatherTempValues,
      }
    case WeatherSource.PREFECTURAL:
    case WeatherSource.WEEKLY:
      return { weatherIcons: [], weatherTemps, weatherTempValues }
    default:
      return { weatherIcons: [], weatherTemps: [], weatherTempValues }
  }
}
